import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route as RouteImpl, RouteProps } from 'react-router';
import { Loader } from '../../ui/loader/loader';
import { mfaEnrollPath } from '../auth/mfaGate';
import { getPermissions, isUserLoggedIn } from '../helpers/auth';
import { hasActiveFeature } from '../helpers/subscriptionEnum';
import { RootState } from '../redux_store/store';

type PublicRouteProps = RouteProps & {
  shouldRedirectIfLoggedIn?: boolean;
};

type PrivateRouteProps = RouteProps & {
  requiredPermissions: string[];
  requiredFeature?: string;
};

export function Route({
  shouldRedirectIfLoggedIn = false,
  ...routeProps
}: PublicRouteProps) {
  if (shouldRedirectIfLoggedIn && isUserLoggedIn()) {
    return <Redirect to='/home' />;
  } else {
    return <RouteImpl {...routeProps} />;
  }
}

export function AdminRoute({ ...routeProps }: RouteProps) {
  const adminToken = localStorage.getItem('adminToken');

  if (!adminToken) {
    return <Redirect to='/admin' />;
  }

  return <RouteImpl {...routeProps} />;
}

export function CandibankRoute({ ...routeProps }: RouteProps) {
  const adminToken = localStorage.getItem('adminToken');

  if (!adminToken) {
    return <Redirect to='/admin' />;
  }

  return <RouteImpl {...routeProps} />;
}

export function AuthenticatedRoute({
  requiredPermissions,
  requiredFeature,
  ...routeProps
}: PrivateRouteProps) {
  const subscriptions = useSelector((state: RootState) => state.subscriptions);
  const mfaEnrollmentRequired = useSelector(
    (state: RootState) => state.authGate.mfaEnrollmentRequired,
  );
  const token = localStorage.getItem('token');

  if (!isUserLoggedIn()) {
    // Preserve where the user was headed so login (password or SSO) can send
    // them straight there afterwards (deep-link, e.g. a specific assessment).
    const attempted =
      window.location.pathname + window.location.search + window.location.hash;
    const search =
      attempted && attempted !== '/'
        ? `?next=${encodeURIComponent(attempted)}`
        : undefined;
    return <Redirect to={{ pathname: '/login', search }} />;
  }

  // The tenant requires two-factor authentication and this user has not set it
  // up: nothing behind the gate is reachable until they do. The attempted path
  // rides along so enrolment lands them where they were going.
  if (mfaEnrollmentRequired) {
    const attempted =
      window.location.pathname + window.location.search + window.location.hash;
    return <Redirect to={mfaEnrollPath(attempted)} />;
  }

  if (requiredPermissions.length === 0) {
    return <RouteImpl {...routeProps} />;
  }

  if (requiredPermissions && token) {
    const permissions = getPermissions(token);
    const accessGranted = requiredPermissions.every((permission) =>
      permissions.includes(permission),
    );
    if (!accessGranted) {
      return <Redirect to='/home' />;
    }
  }

  if (subscriptions.loading !== 'succeeded') {
    return <Loader loading={true} />;
  }

  if (
    requiredFeature &&
    subscriptions.subscriptions &&
    !hasActiveFeature(subscriptions.subscriptions, requiredFeature)
  ) {
    return <Redirect to='/home' />;
  }

  return <RouteImpl {...routeProps} />;
}
